<script lang="ts" setup>
import { Fulltime } from '@/core/collections/contracts-api/company-joint-committees/fulltimes/fulltime.interface';
import Dropdown from '@/core/components/form-elements/Dropdown.vue';

//#region Props & Emits
const props = defineProps<{
   fulltimes: Array<Fulltime>;
   disabled?: boolean;
   errors?: any;
   required?: boolean;
   label?: string;
   name: string;
   isWeekendWorker?: boolean;
}>();

const fulltimeWeekId = defineModel<number | string | null>('modelValue');
//#endregion

//#region Weeks
function isSameId(left: unknown, right: unknown): boolean {
   return left == right;
}

const fulltimeWeeks = computed(() =>
   props.fulltimes
      .reduce((acc, cur) => {
         const mappedWeeks = (cur.weeks || []).map((week) => ({
            ...week,
            fulltime_id: cur.id,
            fulltime: {
               id: cur.id,
               name: cur.name,
               fulltime_weekly_hours: cur.fulltime_weekly_hours,
               is_continuous: cur.is_continuous,
            },
         }));

         return [...acc, ...mappedWeeks];
      }, [] as any)
      .filter((fw: any) => (props.isWeekendWorker ? fw.days_in_week === 2 : true)),
);

const selectedWeek = computed(() => fulltimeWeeks.value.find((week: any) => isSameId(week.id, fulltimeWeekId.value)) || null);
const selectedFulltimeId = computed({
   get: () => selectedWeek.value?.fulltime_id || null,
   set: (val: number | string | null) => {
      if (val === null) {
         fulltimeWeekId.value = null;
      } else {
         const firstWeek = fulltimeWeeks.value.find((week: any) => isSameId(week.fulltime_id, val));
         fulltimeWeekId.value = firstWeek ? firstWeek.id : null;
      }
   },
});
const selectedFulltime = computed(() => props.fulltimes.find((ft) => isSameId(ft.id, selectedFulltimeId.value)) || null);
//#endregion

//#region Error
const error = computed(() => props.errors?.[props.name]);
//#endregion
</script>

<template>
   <div
      class="fulltime-dropdown"
      :class="['nxt-form-group', { '-danger': !!error, '-required': props.required, '-disabled': props.disabled }]"
   >
      <label class="nxt-form-group__label" :for="name || 'fulltime_id'">
         {{ label || $t('companies.fulltimes.fulltime', 1) }}
      </label>
      <div class="fulltime-dropdown__inputs">
         <Dropdown
            class="fulltime-dropdown__select"
            v-bind="$attrs"
            v-model="selectedFulltimeId"
            :placeholder="label || $t('companies.fulltimes.fulltime', 1)"
            :name="'fulltime_id'"
            :disabled="disabled"
            :options="fulltimes"
            :errors="errors"
            :required="required"
            :display-key="'name'"
         >
            <template #option="{ option }">
               <div class="fulltime-dropdown__fulltime-option">
                  <span class="text-nowrap">{{ option.name }}</span>
                  <NxtBadge
                     v-if="option.is_continuous"
                     :background-color="'var(--nxt-blue-300)'"
                     :text-color="'var(--text-primary)'"
                     class="text-nowrap"
                  >
                     {{ $t('companies.fulltimes.continuous-fulltime') }}
                  </NxtBadge>
               </div>
            </template>
         </Dropdown>

         <template v-if="selectedFulltime?.is_continuous">
            <i class="fas fa-chevron-right"></i>

            <Dropdown
               class="fulltime-dropdown__select -week"
               v-bind="$attrs"
               v-model="fulltimeWeekId"
               :placeholder="$t('general.weeks', 1)"
               :name="name || 'fulltime_week_id'"
               :disabled="disabled || !selectedFulltimeId"
               :options="(selectedFulltime.weeks || []).map((week) => ({ ...week, name: `${$t('general.weeks')} ${week.week_number}` }))"
               :errors="errors"
               :required="required"
            >
               <template #option="{ option }">
                  <div class="d-flex flex-column py-2">
                     <strong>{{ option.name }}</strong>
                     <div class="fulltime-dropdown__hours nxt-mono">
                        <div v-for="day in weekdays.map((day, i) => `${shortDays[$i18n.locale][i]}:${option[`hours_${day}`]}`)" :key="day">
                           {{ day }}
                        </div>
                     </div>
                  </div>
               </template>
            </Dropdown>
         </template>
      </div>
   </div>
</template>

<style scoped lang="scss">
.fulltime-dropdown__inputs {
   display: flex;
   align-items: center;

   i {
      color: var(--bs-secondary);
      padding: var(--nxt-gutter);
   }
}
.fulltime-dropdown__select {
   flex: 1;
   min-width: toRem(150);

   &:deep(label) {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
   }

   &.-week {
      width: toRem(100);
   }
}

.fulltime-dropdown__fulltime-option {
   display: flex;
   align-items: center;
   justify-content: space-between;
   gap: var(--nxt-gutter-small);
}

.fulltime-dropdown__hours {
   margin-top: var(--nxt-gutter-small);
   display: grid;
   grid-template-columns: 1fr 1fr;
   gap: var(--nxt-gutter);
}
</style>
